import React, { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { usePortalStore } from '../store';

interface AICoFounderPanelProps {
  currentView: string;
}

const CONTEXT_HINT: Record<string, string> = {
  'company-overview': 'Looking at company health',
  'department': 'Examining department capabilities',
  'gates': 'Reviewing quality gates',
  'documents': 'Browsing governance documents',
  'watchtower': 'Monitoring strategic signals',
  'operations': 'Operations dashboard',
  'governance': 'Governance & compliance',
};

export const AICoFounderPanel: React.FC<AICoFounderPanelProps> = ({ currentView }) => {
  const [message, setMessage] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const { lucasChat, lucasChatLoading, loadLucasChat, sendLucasMessage } = usePortalStore();

  useEffect(() => {
    loadLucasChat();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lucasChat]);

  const handleSend = async () => {
    const text = message.trim();
    if (!text || lucasChatLoading) return;
    setMessage('');
    await sendLucasMessage(text);
  };

  const contextHint = CONTEXT_HINT[currentView] ?? 'Ready to help';
  const displayChat = lucasChat.length > 0 ? lucasChat : [{
    id: 'welcome',
    role: 'lucas' as const,
    content: "I'm Lucas, your AI co-founder. Ask me about the backlog, architecture, or ZPC state. What do you want to move?",
    created_at: new Date().toISOString(),
  }];

  return (
    <div
      className="flex flex-col h-full p-4"
      style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-primary)' }}
    >
      <div
        className="mb-4 pb-4"
        style={{ borderBottom: '1px solid var(--border-subtle)' }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5" style={{ color: 'var(--accent-amber)' }} />
          <h2
            className="font-semibold"
            style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}
          >
            Lucas
          </h2>
        </div>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontFamily: 'var(--font-ui)' }}>
          {contextHint}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 mb-4">
        {displayChat.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-3 ${msg.role === 'founder' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className="max-w-xs px-3 py-2 rounded-lg text-sm"
              style={
                msg.role === 'founder'
                  ? {
                      backgroundColor: 'var(--accent-teal)',
                      color: 'var(--bg-primary)',
                      fontFamily: 'var(--font-ui)',
                    }
                  : {
                      backgroundColor: 'var(--bg-elevated)',
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-ui)',
                    }
              }
            >
              {msg.content}
            </div>
          </div>
        ))}
        {lucasChatLoading && (
          <div className="flex justify-start">
            <div
              className="px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              <Loader2 className="w-3 h-3 animate-spin" />
              Lucas is thinking...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div
        className="flex items-center gap-2 pt-4"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <input
          type="text"
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Message Lucas..."
          disabled={lucasChatLoading}
          className="flex-1 px-3 py-2 rounded-lg text-sm focus:outline-none disabled:opacity-50"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-ui)',
          }}
        />
        <button
          onClick={handleSend}
          disabled={lucasChatLoading || !message.trim()}
          className="p-2 rounded-lg transition-colors disabled:opacity-50"
          style={{
            backgroundColor: 'var(--accent-teal)',
            color: 'var(--bg-primary)',
          }}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
